import { useSyncExternalStore } from 'react'
import type { ApiClientConsentSetting } from '@storefronts/common-types'
import { useIsomorphicLayoutEffect } from '../../hooks/useIsomorphicLayoutEffect'

export const CONSENT_SESSION_STORAGE_KEY = '_bcai_sf_consent'

type Cleanup = () => void

const listeners = new Set<() => void>()

let allowed = false
let acceptedThisTab = false
let configured = false

function notify(): void {
    listeners.forEach((listener) => listener())
}

function readAccepted(): boolean {
    if (acceptedThisTab) return true
    try {
        if (typeof sessionStorage === 'undefined') return false
        return (
            sessionStorage.getItem(CONSENT_SESSION_STORAGE_KEY) === 'accepted'
        )
    } catch {
        return false
    }
}

export function hasAcceptedConsent(): boolean {
    return readAccepted()
}

export function isConsentModalEnabled(
    setting: ApiClientConsentSetting | null | undefined,
    isPreview: boolean,
): boolean {
    return !isPreview && Boolean(setting?.enabled)
}

export function configureConsentTracking(
    setting: ApiClientConsentSetting | null | undefined,
    isPreview: boolean,
): void {
    const modalEnabled = isConsentModalEnabled(setting, isPreview)
    const deferUntilAccept = Boolean(setting?.deferTrackingUntilAccept)
    const nextAllowed = !modalEnabled || !deferUntilAccept || readAccepted()
    configured = true
    if (nextAllowed === allowed) return
    allowed = nextAllowed
    notify()
}

export function isStorageAndTrackingAllowed(): boolean {
    return allowed
}

/**
 * Whether a landing page has configured the gate in this document. Documents
 * without one (crowlink redirects, the desktop preview shell) deny storage by
 * default but have no modal that could ever open the gate.
 */
export function isConsentGateConfigured(): boolean {
    return configured
}

export function markConsentAccepted(): void {
    acceptedThisTab = true
    try {
        sessionStorage.setItem(CONSENT_SESSION_STORAGE_KEY, 'accepted')
    } catch {
        // Private mode / blocked storage should still unlock this tab.
    }
    if (allowed) return
    allowed = true
    notify()
}

function subscribe(onStoreChange: () => void): Cleanup {
    listeners.add(onStoreChange)
    return () => {
        listeners.delete(onStoreChange)
    }
}

/**
 * Runs `callback` once tracking is allowed: synchronously if it already is,
 * otherwise on the accept that unlocks it. For starting third-party SDKs
 * (Datadog, Mux) after consent. The returned cleanup cancels a callback that
 * has not fired yet.
 */
export function whenTrackingAllowed(callback: () => void): Cleanup {
    if (allowed) {
        callback()
        return () => {}
    }
    const listener = () => {
        if (!allowed) return
        listeners.delete(listener)
        // Runs inside notify(); a throwing SDK must not stop the remaining
        // listeners (React subscriptions) from learning the gate opened.
        try {
            callback()
        } catch (e) {
            console.error(e)
        }
    }
    listeners.add(listener)
    return () => {
        listeners.delete(listener)
    }
}

export function useTrackingAllowed(): boolean {
    return useSyncExternalStore(
        subscribe,
        isStorageAndTrackingAllowed,
        isStorageAndTrackingAllowed,
    )
}

export function useConsentTrackingGate(
    setting: ApiClientConsentSetting | null | undefined,
    isPreview: boolean,
): boolean {
    // Layout (not render): PixelLoader / useAnalytics subscribe from a
    // parent or sibling and must not be notified while this tree renders.
    useIsomorphicLayoutEffect(() => {
        configureConsentTracking(setting, isPreview)
    }, [setting, isPreview])
    return useTrackingAllowed()
}

export function resetConsentTracking(): void {
    allowed = false
    acceptedThisTab = false
    configured = false
    listeners.clear()
}
